import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bugtracker, useIdentityContext } from '@99designs/design-services-common';
import {
    useBriefContext,
    useCustomerIdentifiedMutation,
} from '@99designs/feature-design-services-brief';
import { __url } from '@99designs/i18n';

interface UseBriefRedirectsParams {
    correlationId?: string | null;
    briefState?: string | null;
    currentUrl?: string | null;
}

export function useBriefRedirects({
    correlationId,
    briefState,
    currentUrl,
}: UseBriefRedirectsParams) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isSignedIn } = useIdentityContext();
    const { isRequestQueryLoading, isRequestClaimed, workId, requestExperiments } =
        useBriefContext();
    const [customerIdentified, { error: customerIdentifiedError }] =
        useCustomerIdentifiedMutation();

    const isChatBriefDisabled = searchParams.get('chatBrief') === 'false';
    const isRedirectToStudio = searchParams.get('redirectToStudio') === '1';
    const isRedirectToDraftBriefs = searchParams.get('isRedirectToDrafts') || '0';
    const hasChatBriefExperiment = requestExperiments?.includes('chat_brief') ?? false;

    const customerSignedIn = useCallback(async () => {
        await customerIdentified({
            variables: { requestId: correlationId ?? '' },
        });

        if (isRedirectToStudio && workId) {
            window.location.href = `${__url('/studio/')}?workId=${workId}`;
            return;
        }

        if (['1', 'true'].includes(isRedirectToDraftBriefs.toLowerCase())) {
            const destination = __url('/graphic-design/dashboard?tab=draft');
            if (destination.length > 0) {
                window.location.href = destination;
                return;
            }

            bugtracker.notify(new Error('could not localise graphic design dashboard URL'));
        }
    }, [customerIdentified, correlationId, isRedirectToDraftBriefs, isRedirectToStudio, workId]);

    useEffect(() => {
        if (!isRequestQueryLoading && hasChatBriefExperiment && !isChatBriefDisabled) {
            void navigate('chat', { replace: true });
        }
    }, [isRequestQueryLoading, hasChatBriefExperiment, isChatBriefDisabled, navigate]);

    // Claim the brief if the user is signed in and the request is unclaimed.
    useEffect(() => {
        if (isSignedIn && isRequestClaimed === false && correlationId) {
            void customerSignedIn();
        }
    }, [isSignedIn, correlationId, isRequestClaimed, customerSignedIn]);

    useEffect(() => {
        if (briefState === 'LAUNCHED') {
            if (currentUrl && currentUrl !== window.location.pathname) {
                window.location.href = currentUrl;
            } else {
                window.location.href = __url('/graphic-design/dashboard');
            }
        }
    }, [briefState, currentUrl]);

    return {
        customerIdentifiedError,
        isRedirecting: hasChatBriefExperiment && !isChatBriefDisabled,
    };
}
